import { supabase } from '../lib/supabase';
import type { ContactLink } from '../types/contact';

const STORAGE_KEY = 'activelegend_contact_links_v2';
const EVENT_NAME = 'activelegend_contact_links_change';

const DEFAULT_LINKS: ContactLink[] = [
  {
    id: 'link-email-studio',
    label: 'ایمیل رسمی استودیو',
    url: 'mailto:studio@activelegend.ir',
    value: 'studio@activelegend.ir',
    icon: 'email',
    category: 'support',
    sort_order: 1,
    is_active: true,
    created_at: '2026-01-01T00:00:00.000Z',
  },
  {
    id: 'link-tg-channel',
    label: 'کانال رسمی تلگرام',
    url: 'https://t.me/ActiveLegend_ir',
    value: '@ActiveLegend_ir',
    icon: 'telegram',
    category: 'social',
    sort_order: 2,
    is_active: true,
    created_at: '2026-01-01T00:00:00.000Z',
  },
  {
    id: 'link-tg-group',
    label: 'گروه گفتگوی تلگرام',
    url: 'https://t.me/ActiveLegendGroup',
    value: '@ActiveLegendGroup',
    icon: 'telegram',
    category: 'social',
    sort_order: 3,
    is_active: true,
    created_at: '2026-01-01T00:00:00.000Z',
  },
  {
    id: 'link-instagram',
    label: 'اینستاگرام رسمی',
    url: 'https://www.instagram.com/activelegend.ir',
    value: '@activelegend.ir',
    icon: 'instagram',
    category: 'social',
    sort_order: 4,
    is_active: true,
    created_at: '2026-01-01T00:00:00.000Z',
  },
];

class ContactLinksService {
  private cache: ContactLink[] = [];
  public isCloudConnected = false;
  public cloudError: string | null = null;

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        this.cache = JSON.parse(stored);
      } else {
        this.cache = [...DEFAULT_LINKS];
        this.saveToStorage();
      }
    } catch {
      this.cache = [...DEFAULT_LINKS];
    }
  }

  private saveToStorage() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.cache));
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(EVENT_NAME));
      }
    } catch (e) {
      console.warn('Could not save contact links to localStorage:', e);
    }
  }

  /**
   * Fetch all contact links from Supabase with fallback to cache
   */
  public async getAll(): Promise<ContactLink[]> {
    try {
      const { data, error } = await supabase
        .from('contact_links')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) {
        this.isCloudConnected = false;
        this.cloudError = error.message;
        console.warn('Supabase contact_links table query error:', error.message);
      } else if (data && data.length > 0) {
        this.isCloudConnected = true;
        this.cloudError = null;
        this.cache = data as ContactLink[];
        this.saveToStorage();
        return this.cache;
      } else if (data && data.length === 0) {
        this.isCloudConnected = true;
        this.cloudError = null;
        // Table exists but is empty, seed defaults
        for (const item of DEFAULT_LINKS) {
          await supabase.from('contact_links').insert([item]);
        }
        return [...DEFAULT_LINKS];
      }
    } catch (e: any) {
      this.isCloudConnected = false;
      this.cloudError = e?.message || 'خطای اتصال به دیتابیس ابری';
    }

    return [...this.cache].sort((a, b) => a.sort_order - b.sort_order);
  }

  /**
   * Fetch only active contact links (for public display)
   */
  public async getActive(): Promise<ContactLink[]> {
    const all = await this.getAll();
    return all.filter((link) => link.is_active);
  }

  /**
   * Create a new contact link
   */
  public async create(linkData: Omit<ContactLink, 'id' | 'created_at' | 'updated_at'>): Promise<ContactLink> {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    const newLink: ContactLink = {
      ...linkData,
      id,
      created_at: now,
      updated_at: now,
    };

    // Try Supabase insert
    const { data, error } = await supabase
      .from('contact_links')
      .insert([newLink])
      .select()
      .single();

    if (error) {
      this.isCloudConnected = false;
      this.cloudError = error.message;
      // Also save to cache so current session doesn't lose data
      this.cache.push(newLink);
      this.saveToStorage();
      throw new Error(
        `خطا در ثبت در دیتابیس ابری Supabase (${error.message}). لطفاً اسکریپت SQL را در داشبورد Supabase اجرا کنید تا تغییرات در تمام مرورگرها همگام شود.`
      );
    }

    this.isCloudConnected = true;
    this.cloudError = null;
    this.cache.push(data as ContactLink);
    this.saveToStorage();
    return data as ContactLink;
  }

  /**
   * Update an existing contact link
   */
  public async update(id: string, updates: Partial<ContactLink>): Promise<ContactLink> {
    const now = new Date().toISOString();
    const index = this.cache.findIndex((l) => l.id === id);
    if (index === -1) {
      throw new Error('لینک مورد نظر یافت نشد.');
    }

    const updatedItem: ContactLink = {
      ...this.cache[index],
      ...updates,
      updated_at: now,
    };

    // Try Supabase update
    const { data, error } = await supabase
      .from('contact_links')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      this.isCloudConnected = false;
      this.cloudError = error.message;
      this.cache[index] = updatedItem;
      this.saveToStorage();
      throw new Error(
        `خطا در بروزرسانی دیتابیس Supabase (${error.message}). لطفاً اسکریپت SQL را در داشبورد Supabase اجرا کنید تا در سایر مرورگرها نیز اعمال شود.`
      );
    }

    this.isCloudConnected = true;
    this.cloudError = null;
    this.cache[index] = data as ContactLink;
    this.saveToStorage();
    return data as ContactLink;
  }

  /**
   * Delete a contact link
   */
  public async delete(id: string): Promise<boolean> {
    const { error } = await supabase.from('contact_links').delete().eq('id', id);

    if (error) {
      this.isCloudConnected = false;
      this.cloudError = error.message;
      this.cache = this.cache.filter((l) => l.id !== id);
      this.saveToStorage();
      throw new Error(
        `خطا در حذف از Supabase (${error.message}). لطفاً اسکریپت SQL را در دیتابیس اجرا کنید.`
      );
    }

    this.isCloudConnected = true;
    this.cloudError = null;
    this.cache = this.cache.filter((l) => l.id !== id);
    this.saveToStorage();
    return true;
  }

  /**
   * Reorder links by ordered IDs
   */
  public async reorder(orderedIds: string[]): Promise<void> {
    const updated = this.cache.map((item) => {
      const newOrder = orderedIds.indexOf(item.id);
      return newOrder !== -1 ? { ...item, sort_order: newOrder + 1 } : item;
    });

    this.cache = updated.sort((a, b) => a.sort_order - b.sort_order);
    this.saveToStorage();

    for (let i = 0; i < orderedIds.length; i++) {
      try {
        await supabase
          .from('contact_links')
          .update({ sort_order: i + 1 })
          .eq('id', orderedIds[i]);
      } catch {
        // Silently continue
      }
    }
  }

  /**
   * Subscribe to live updates
   */
  public subscribe(callback: () => void): () => void {
    if (typeof window === 'undefined') return () => {};
    window.addEventListener(EVENT_NAME, callback);
    return () => window.removeEventListener(EVENT_NAME, callback);
  }
}

export const contactLinksService = new ContactLinksService();
